import type {
  CUSTOM_DOMAIN_STATUSES,
  CustomDomainDnsRecord,
} from "../../../shared/customDomains";
import type { CustomDomainSetup } from "../../../shared/generatedDrizzleSchema";
import type { StatusBadgeColor } from "../StatusBadge";

const domainStatus = {
  pending: "Preparing",
  dns: "Misconfigured",
  tls: "Finishing setup",
  ready: "Verified",
  removing: "Removing",
  error: "Failed",
  expired: "Expired",
};

const statusColor: Record<
  (typeof CUSTOM_DOMAIN_STATUSES)[number],
  StatusBadgeColor
> = {
  pending: "gray",
  dns: "red",
  tls: "gray",
  ready: "green",
  removing: "gray",
  error: "red",
  expired: "gray",
};

export const settingUp = (setup: CustomDomainSetup) =>
  !setup.activatedAt && ["pending", "dns", "tls"].includes(setup.status);

export const finishingSetup = (setup: CustomDomainSetup) =>
  settingUp(setup) &&
  (setup.status === "tls" ||
    (!setup.lastError &&
      setup.dnsRecords.length > 0 &&
      setup.dnsRecords.every((record) => record.verified)));

type Badge = { label: string; color: StatusBadgeColor };

export function statusBadge(setup: CustomDomainSetup): Badge {
  if (!settingUp(setup))
    return {
      label: domainStatus[setup.status],
      color: statusColor[setup.status],
    };
  if (finishingSetup(setup)) return { label: domainStatus.tls, color: "gray" };
  if (!setup.dnsRecords.length)
    return { label: domainStatus.pending, color: "gray" };
  return { label: "Pending", color: "gray" };
}

export function recordBadge(
  setup: CustomDomainSetup,
  record: CustomDomainDnsRecord
): Badge {
  if (!record.verified) return { label: "Pending", color: "gray" };
  if (setup.activatedAt) return { label: "Verified", color: "green" };
  return { label: "Detected", color: "green" };
}

export function attentionMessage(setup: CustomDomainSetup) {
  if (setup.status === "expired")
    return "This setup expired. Delete the domain and add it again to restart verification.";
  const waitingForRecords =
    settingUp(setup) &&
    setup.status === "dns" &&
    setup.dnsRecords.some((record) => !record.verified);
  if (setup.lastError && !waitingForRecords) return setup.lastError;
  if (
    setup.status === "error" ||
    (setup.activatedAt && setup.status !== "ready")
  )
    return "We’ll keep checking automatically.";
  return null;
}
